import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { Shirt, Save, AlertTriangle, Crown, Store, Plus, Trash2 } from 'lucide-react';

const KINDS = [
    { value: 'laundry', label: 'Solo Lavandería' },
    { value: 'drycleaning', label: 'Solo Tintorería' },
    { value: 'both', label: 'Ambos (Lavandería + Tintorería)' },
];

const UNITS = [
    { value: 'kg', label: 'Kg' },
    { value: 'piece', label: 'Pieza' },
    { value: 'bundle', label: 'Paquete' },
];

export default function DashboardMyServices() {
    const [quota, setQuota] = useState(null);
    const [provider, setProvider] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: '', kind: 'laundry', description: '', pickup_area_km: 5,
        accepts_pickup: true, accepts_dropoff: true, phone: '', address: '', notes_for_pickup: '',
        pricelist: null,
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: q }, { data: p }] = await Promise.all([
                api.get('/me/plan/quota'),
                api.get('/me/services/provider'),
            ]);
            setQuota(q);
            setProvider(p.provider);
            if (p.provider) {
                setForm({
                    name: p.provider.name || '',
                    kind: p.provider.kind || 'laundry',
                    description: p.provider.description || '',
                    pickup_area_km: p.provider.pickup_area_km || 5,
                    accepts_pickup: p.provider.accepts_pickup !== false,
                    accepts_dropoff: p.provider.accepts_dropoff !== false,
                    phone: p.provider.phone || '',
                    address: p.provider.address || '',
                    notes_for_pickup: p.provider.notes_for_pickup || '',
                    pricelist: p.provider.pricelist || [],
                });
            }
        } catch (e) { toast.error('Error al cargar'); } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put('/me/services/provider', form);
            toast.success('Perfil de servicios guardado');
            setProvider(data.provider);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); } finally { setBusy(false); }
    };

    const feats = quota?.features || {};
    const enabled = !!feats.services_provider_enabled;
    const maxKinds = Number(feats.services_max_kinds || 0);
    const kindOptions = maxKinds >= 2 ? KINDS : KINDS.filter(k => k.value !== 'both');

    if (loading) return <div className="text-sm text-muted-foreground py-10 text-center">Cargando…</div>;

    return (
        <div className="space-y-4" data-testid="dashboard-my-services">
            <div className="flex items-center gap-3 flex-wrap">
                <Shirt className="h-6 w-6 text-[hsl(var(--primary))]" />
                <h1 className="text-2xl font-semibold">Mis servicios (Lavandería/Tintorería)</h1>
                {enabled ? (
                    <Badge className="bg-emerald-100 text-emerald-800">Habilitado</Badge>
                ) : (
                    <Badge variant="outline" className="border-amber-300 text-amber-700">Requiere plan Starter+</Badge>
                )}
                <div className="ml-auto">
                    <Button variant="outline" size="sm" asChild><Link to="/servicios"><Store className="h-4 w-4 mr-1" />Ver hub público</Link></Button>
                </div>
            </div>

            {!enabled && (
                <Alert className="border-amber-200 bg-amber-50" data-testid="services-plan-gate">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        Tu plan actual (<strong>{quota?.plan_name || quota?.plan_slug}</strong>) no incluye Mis Servicios.
                        {' '}<Link to="/planes" className="underline text-primary">Actualiza a Starter o superior</Link> para ofrecer lavandería/tintorería en OkStore.
                    </AlertDescription>
                </Alert>
            )}

            <Card>
                <CardContent className="p-5 space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                        <div className="text-sm font-semibold flex-1">Configuración de tu servicio</div>
                        <Badge variant="outline"><Crown className="h-3 w-3 mr-1" />Plan {quota?.plan_slug || 'free'}</Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label>Nombre del servicio</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Lavandería Aqua Express" data-testid="service-name" disabled={!enabled} />
                        </div>
                        <div>
                            <Label>Tipo de servicio</Label>
                            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })} disabled={!enabled}>
                                <SelectTrigger data-testid="service-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {kindOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            {maxKinds < 2 && <p className="text-[10px] text-muted-foreground mt-1">Tu plan permite un solo tipo. Actualiza a Pro para ofrecer ambos.</p>}
                        </div>
                        <div className="md:col-span-2">
                            <Label>Descripción</Label>
                            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Cuéntale a tus clientes sobre tu servicio…" data-testid="service-description" disabled={!enabled} />
                        </div>
                        <div>
                            <Label>Teléfono</Label>
                            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+52 55 1234 5678" disabled={!enabled} />
                        </div>
                        <div>
                            <Label>Radio de servicio (km)</Label>
                            <Input type="number" min="1" max="50" value={form.pickup_area_km} onChange={(e) => setForm({ ...form, pickup_area_km: parseInt(e.target.value) || 5 })} disabled={!enabled} />
                        </div>
                        <div className="md:col-span-2">
                            <Label>Dirección del local</Label>
                            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Calle y número, colonia, ciudad" disabled={!enabled} />
                        </div>
                        <div className="md:col-span-2">
                            <Label>Notas para recoger la ropa</Label>
                            <Textarea rows={2} value={form.notes_for_pickup} onChange={(e) => setForm({ ...form, notes_for_pickup: e.target.value })} placeholder="Horario de recolección, cómo identificar la ropa…" disabled={!enabled} />
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded">
                            <div>
                                <div className="text-sm font-medium">Acepta pickup a domicilio</div>
                                <p className="text-xs text-muted-foreground">Recoges la ropa del cliente</p>
                            </div>
                            <Switch checked={form.accepts_pickup} onCheckedChange={(v) => setForm({ ...form, accepts_pickup: v })} disabled={!enabled} data-testid="service-pickup-switch" />
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded">
                            <div>
                                <div className="text-sm font-medium">Acepta dropoff en local</div>
                                <p className="text-xs text-muted-foreground">Cliente lleva la ropa</p>
                            </div>
                            <Switch checked={form.accepts_dropoff} onCheckedChange={(v) => setForm({ ...form, accepts_dropoff: v })} disabled={!enabled} data-testid="service-dropoff-switch" />
                        </div>
                    </div>
                    {/* Pricelist editor (Phase 20f) */}
                    <div className="border-t pt-4">
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <div className="text-sm font-semibold">Lista de precios</div>
                                <p className="text-xs text-muted-foreground">Los clientes verán estos precios al reservar tu servicio.</p>
                            </div>
                            <Button
                                type="button" size="sm" variant="outline"
                                disabled={!enabled}
                                onClick={() => setForm(f => ({ ...f, pricelist: [...(f.pricelist || []), { key: `item_${Date.now()}`, label: 'Nuevo item', unit: 'piece', price: 0 }] }))}
                                data-testid="add-price-item"
                            ><Plus className="h-4 w-4 mr-1" />Agregar</Button>
                        </div>
                        <div className="space-y-2">
                            {(form.pricelist || []).length === 0 && (
                                <div className="text-center text-xs text-muted-foreground py-4 border-2 border-dashed rounded">Sin items. Añade tu primero.</div>
                            )}
                            {(form.pricelist || []).map((it, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded border bg-slate-50/40" data-testid={`price-item-${idx}`}>
                                    <div className="col-span-5">
                                        <Label className="text-xs">Descripción</Label>
                                        <Input value={it.label || ''} disabled={!enabled} onChange={(e) => setForm(f => ({ ...f, pricelist: f.pricelist.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) }))} placeholder="Camisa, kilo, paquete…" data-testid={`price-label-${idx}`} />
                                    </div>
                                    <div className="col-span-3">
                                        <Label className="text-xs">Clave</Label>
                                        <Input value={it.key || ''} disabled={!enabled} onChange={(e) => setForm(f => ({ ...f, pricelist: f.pricelist.map((x, i) => i === idx ? { ...x, key: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() } : x) }))} placeholder="camisa" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-xs">Unidad</Label>
                                        <Select value={it.unit || 'piece'} disabled={!enabled} onValueChange={(v) => setForm(f => ({ ...f, pricelist: f.pricelist.map((x, i) => i === idx ? { ...x, unit: v } : x) }))}>
                                            <SelectTrigger data-testid={`price-unit-${idx}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>{UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-1">
                                        <Label className="text-xs">$</Label>
                                        <Input type="number" min="0" step="1" value={it.price ?? 0} disabled={!enabled} onChange={(e) => setForm(f => ({ ...f, pricelist: f.pricelist.map((x, i) => i === idx ? { ...x, price: parseFloat(e.target.value) || 0 } : x) }))} data-testid={`price-value-${idx}`} />
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        <Button type="button" size="sm" variant="ghost" disabled={!enabled} onClick={() => setForm(f => ({ ...f, pricelist: f.pricelist.filter((_, i) => i !== idx) }))} data-testid={`price-remove-${idx}`}>
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end pt-2 border-t">
                        <Button onClick={save} disabled={!enabled || busy || !form.name} data-testid="save-service"><Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : 'Guardar servicio'}</Button>
                    </div>
                    {provider && (
                        <div className="text-xs text-muted-foreground border-t pt-3">
                            <div>Estado: <Badge variant="outline">{provider.active ? 'Activo' : 'Inactivo'}</Badge></div>
                            <div className="mt-1">Ver como público: <Link to={`/servicios/${provider.id}`} className="text-primary underline">/servicios/{provider.id}</Link></div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
